/**
 * Step 4: Review & Create
 * 
 * Shows summary of all selections before creating the address.
 * Includes preview, fee information, and optional label input.
 * 
 * @module addresses/wizard
 */

import React from 'react';
import { Heading } from "@/components/ui/typography";
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { NetworkIcon } from '@/components/NetworkIcons';
import type { WizardFormData } from './types';
import { NETWORK_LABELS, ASSET_INFO, PAYMENT_RAIL_INFO } from './types';

interface ReviewStepProps {
  formData: WizardFormData;
  label: string;
  onLabelChange: (label: string) => void;
  onEditStep: (step: number) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  label,
  onLabelChange,
  onEditStep,
}) => {
  const isFiat = formData.destinationType === 'fiat';
  const isCrypto = formData.destinationType === 'crypto';

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Heading level={2} className={cn("text-xl sm:text-heading-lg", semanticTokens.fg.default)}>
          Review Your Address
        </Heading>
        <p className={cn("text-sm sm:text-base", semanticTokens.fg.muted)}>
          Confirm your settings before creating the address
        </p>
      </div>

      <div className={cn(
        "rounded-xl border overflow-hidden",
        semanticTokens.bg.surface,
        semanticTokens.border.default
      )}>
        <div className={cn("p-4 border-b", semanticTokens.border.subtle)}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={cn("text-sm font-medium", semanticTokens.fg.muted)}>
              You'll Accept
            </h3>
            <Button variant="ghost" size="sm" onClick={() => onEditStep(2)} className="h-7 text-xs gap-1">
              <SFIcon icon={sfIcons.edit} size="sm" />
              Edit
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", semanticTokens.bg.muted)}>
              <CurrencyIcon symbol={formData.asset} size="md" />
            </div>
            <div>
              <p className={cn("font-medium", semanticTokens.fg.default)}>
                {ASSET_INFO[formData.asset].name}
              </p>
              <div className="flex items-center gap-1.5">
                <NetworkIcon network={formData.network} className="w-3 h-3" />
                <span className={cn("text-sm", semanticTokens.fg.muted)}>
                  on {NETWORK_LABELS[formData.network]}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={cn("p-4 border-b", semanticTokens.border.subtle)}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={cn("text-sm font-medium", semanticTokens.fg.muted)}>
              {isFiat ? "Send To" : "Forward To"}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => onEditStep(3)} className="h-7 text-xs gap-1">
              <SFIcon icon={sfIcons.edit} size="sm" />
              Edit
            </Button>
          </div>
          
          {isFiat ? (
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", semanticTokens.bg.muted)}>
                <SFIcon icon={sfIcons.landmark} size="md" className="text-primary" />
              </div>
              <div>
                <p className={cn("font-medium", semanticTokens.fg.default)}>Bank Account</p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">{formData.currency}</Badge>
                  {formData.paymentRail && (
                    <span className={cn("text-sm", semanticTokens.fg.muted)}>
                      via {PAYMENT_RAIL_INFO[formData.paymentRail]?.name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : isCrypto ? (
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", semanticTokens.bg.muted)}>
                <SFIcon icon={sfIcons.wallet} size="md" className={semanticTokens.fg.accent} />
              </div>
              <div className="min-w-0">
                <p className={cn("font-medium", semanticTokens.fg.default)}>Crypto Wallet</p>
                <p className={cn("text-sm font-mono truncate max-w-[200px] sm:max-w-[300px]", semanticTokens.fg.muted)}>
                  {formData.destinationAddress}
                </p>
                {formData.destinationChain && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <NetworkIcon network={formData.destinationChain} className="w-3 h-3" />
                    <span className={cn("text-xs", semanticTokens.fg.subtle)}>
                      {formData.destinationAsset} on {NETWORK_LABELS[formData.destinationChain]}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {isFiat && (
          <div className={cn("p-4", semanticTokens.bg.canvas)}>
            <h3 className={cn("text-sm font-medium mb-3", semanticTokens.fg.muted)}>
              Conversion Details
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className={semanticTokens.fg.muted}>Conversion Fee</span>
                <span className={semanticTokens.fg.default}>~2%</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className={semanticTokens.fg.muted}>Processing Time</span>
                <span className={semanticTokens.fg.default}>
                  {formData.paymentRail && PAYMENT_RAIL_INFO[formData.paymentRail]?.speed}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className={semanticTokens.fg.muted}>You'll Receive</span>
                <span className={cn("font-medium", semanticTokens.fg.default)}>
                  {formData.currency} in your bank account
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="address-label" className={semanticTokens.fg.muted}>
          Address Label (Optional)
        </Label>
        <Input
          id="address-label"
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder="e.g., Client A, Invoice #123"
          maxLength={50}
        />
        <p className={cn("text-xs", semanticTokens.fg.subtle)}>
          A friendly name to help you identify this address later
        </p>
      </div>

      <div className={cn(
        "p-4 rounded-lg border flex items-start gap-3",
        semanticTokens.info.bg,
        semanticTokens.info.border
      )}>
        <SFIcon icon={sfIcons.info} size="md" className={cn("shrink-0 mt-0.5", semanticTokens.info.fg)} />
        <div className="space-y-1">
          <p className={cn("text-sm font-medium", semanticTokens.info.fg)}>What happens next</p>
          <p className={cn("text-sm", semanticTokens.fg.muted)}>
            {isFiat ? (
              <>
                When someone sends {formData.asset} to your address, we'll automatically convert it 
                to {formData.currency} and send it to your bank account.
              </>
            ) : (
              <>
                When someone sends crypto to your address, we'll automatically forward it to 
                your wallet address.
              </>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <p className={cn("text-sm font-medium", semanticTokens.fg.default)}>Before you continue:</p>
        <div className="space-y-1.5">
          {[
            'I understand this address is permanent',
            isFiat && 'I have verified my bank account details',
            isCrypto && 'I have double-checked the destination address',
            'I understand conversion fees will apply',
          ].filter(Boolean).map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className={cn("w-4 h-4 rounded-full flex items-center justify-center", semanticTokens.positive.bg)}>
                <SFIcon icon={sfIcons.check} size="sm" className={semanticTokens.positive.fg} />
              </div>
              <span className={cn("text-sm", semanticTokens.fg.muted)}>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;

